import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { getAppBasePath } from "../../utils/appPaths";
import { formatDateTime } from "../../utils/dateTime";

const fmtRs = (n, { decimals = 0 } = {}) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatDateStatement = (unix) => formatDateTime(unix);

/** Debit = loan given (out). Credit = repayment received (in). */
const getLoanDrCr = (row) => {
  const amount = Number(row.amount || 0);
  const isDisbursement = row.entryType === "disbursement";
  if (isDisbursement) return { amount, isCredit: false };
  return { amount, isCredit: true };
};

const buildDescription = (row, showParty) => {
  const parts = [];
  if (showParty && row.partyName) {
    parts.push(row.partyName);
  }
  if (row.entryType === "disbursement") {
    parts.push("Loan given");
    if (row.marka) parts.push(`Marka ${row.marka}`);
    if (row.bichak_no) parts.push(`Bichak ${row.bichak_no}`);
  } else {
    parts.push("Received back");
  }
  if (row.accountName && row.accountName !== "—") {
    parts.push(String(row.accountName).toUpperCase());
  }
  if (row.desc?.trim()) parts.push(row.desc.trim());
  return parts.join(" · ");
};

const sortChronological = (a, b) => {
  if (a.date !== b.date) return a.date - b.date;
  if (a.entryType !== b.entryType) {
    return a.entryType === "disbursement" ? -1 : 1;
  }
  return String(a._id).localeCompare(String(b._id));
};

const FILTER_TABS = [
  { id: "all", label: "All" },
  { id: "credit", label: "Credit" },
  { id: "debit", label: "Debit" },
  { id: "deleted", label: "Deleted" },
];

const LoanLogTransactionCard = ({
  row,
  showParty,
  base,
  navigate,
  onEditDisbursement,
  onEditRepayment,
  onDeleteDisbursement,
  onDeleteRepayment,
}) => {
  const deleted = row.isDeleted === true;
  const { amount, isCredit } = getLoanDrCr(row);
  const description = buildDescription(row, showParty);
  const canEdit =
    !deleted &&
    ((row.entryType === "disbursement" && onEditDisbursement) ||
      (row.entryType === "repayment" && onEditRepayment));
  const canDelete =
    !deleted &&
    ((row.entryType === "disbursement" && onDeleteDisbursement) ||
      (row.entryType === "repayment" && onDeleteRepayment));
  const showActions = canEdit || canDelete;

  return (
    <article
      className={`rounded-2xl border shadow-soft overflow-hidden font-quicksand ${
        deleted
          ? "bg-subtle border-line opacity-80"
          : "bg-card border-line"
      }`}
    >
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div
          className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            deleted
              ? "bg-line text-muted"
              : isCredit
                ? "bg-successSoft"
                : "bg-dangerSoft"
          }`}
        >
          {isCredit ? (
            <FaArrowDown
              className={`text-base ${deleted ? "text-muted" : "text-success"}`}
              aria-hidden
            />
          ) : (
            <FaArrowUp
              className={`text-base ${deleted ? "text-muted" : "text-danger"}`}
              aria-hidden
            />
          )}
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p
              className={`text-xl sm:text-2xl font-extrabold tabular-nums leading-none tracking-tight ${
                deleted
                  ? "text-muted line-through"
                  : isCredit
                    ? "text-success"
                    : "text-danger"
              }`}
            >
              PKR {fmtRs(amount)}
            </p>
            {deleted && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-dangerSoft text-danger">
                Deleted
              </span>
            )}
          </div>
          <p
            className={`text-sm mt-2 leading-snug truncate ${
              deleted ? "text-muted" : "text-ink"
            }`}
          >
            {description}
          </p>
          {deleted && (
            <p className="text-xs text-muted mt-1.5">
              Removed {formatDateStatement(row.deletedAt)}
              {row.deletedByName ? ` · by ${row.deletedByName}` : ""}
            </p>
          )}
          {showParty && row.partyName && row.loanAccountId && !deleted && (
            <button
              type="button"
              className="text-xs text-danger font-semibold mt-1.5 hover:underline truncate max-w-full block text-left"
              onClick={() => navigate(`${base}/loans/${row.loanAccountId}`)}
            >
              View {row.partyName}
            </button>
          )}
        </div>
      </div>

      <div className="mx-4 border-t border-line" />

      <div className="px-4 py-3 flex items-center justify-between gap-3 text-sm text-muted">
        <span className="shrink-0">{formatDateStatement(row.date)}</span>
        {!deleted ? (
          <span className="tabular-nums font-medium text-right truncate">
            Balance: PKR{" "}
            {row.balanceAfter != null
              ? fmtRs(row.balanceAfter, { decimals: 2 })
              : "—"}
          </span>
        ) : (
          <span className="text-xs font-semibold text-muted">
            Not in balance
          </span>
        )}
      </div>

      {showActions && (
        <div className="px-4 pb-3 flex justify-end gap-4">
          {canEdit && (
            <button
              type="button"
              className="text-xs font-semibold text-main hover:underline py-1"
              onClick={() => {
                if (row.entryType === "disbursement" && onEditDisbursement) {
                  onEditDisbursement(row);
                } else if (row.entryType === "repayment" && onEditRepayment) {
                  onEditRepayment(row);
                }
              }}
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="text-xs font-semibold text-danger hover:underline py-1"
              onClick={() => {
                if (row.entryType === "disbursement" && onDeleteDisbursement) {
                  onDeleteDisbursement({ _id: row.sourceId });
                } else if (row.entryType === "repayment" && onDeleteRepayment) {
                  onDeleteRepayment({ _id: row.sourceId });
                }
              }}
            >
              Remove
            </button>
          )}
        </div>
      )}
    </article>
  );
};

const LoanTransactionLogCards = ({
  entries = [],
  emptyMessage = "No loan transactions yet.",
  showParty = true,
  statementHeader = null,
  onEditDisbursement,
  onEditRepayment,
  onDeleteDisbursement,
  onDeleteRepayment,
}) => {
  const navigate = useNavigate();
  const base = getAppBasePath();
  const [activeFilter, setActiveFilter] = useState("all");

  const sorted = useMemo(() => {
    const rows = Array.isArray(entries) ? entries : [];
    return [...rows].sort(sortChronological);
  }, [entries]);

  const displayRows = useMemo(() => [...sorted].reverse(), [sorted]);

  const filteredRows = useMemo(() => {
    if (activeFilter === "deleted") {
      return displayRows.filter((row) => row.isDeleted === true);
    }
    const active = displayRows.filter((row) => row.isDeleted !== true);
    if (activeFilter === "all") return active;
    return active.filter((row) => {
      const { isCredit } = getLoanDrCr(row);
      if (activeFilter === "credit") return isCredit;
      return !isCredit;
    });
  }, [displayRows, activeFilter]);

  const deletedCount = useMemo(
    () => displayRows.filter((row) => row.isDeleted === true).length,
    [displayRows]
  );

  if (!sorted.length) {
    return (
      <div className="text-center py-12 rounded-xl border border-dashed border-line text-muted text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="font-quicksand">
      {statementHeader && (
        <div className="flex flex-col max440:gap-1 sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 px-0.5">
          <p className="text-lg font-bold text-main leading-tight">
            {statementHeader.title}
          </p>
          {statementHeader.subtitle && (
            <p className="text-xs text-inkSecondary sm:text-right sm:shrink-0 sm:max-w-[50%]">
              {statementHeader.subtitle}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {FILTER_TABS.map((f) => {
          const active = activeFilter === f.id;
          const label =
            f.id === "deleted" && deletedCount > 0
              ? `Deleted (${deletedCount})`
              : f.label;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? f.id === "deleted"
                    ? "bg-dangerSoft border-danger/40 text-danger"
                    : "bg-brandSoft border-main/40 text-main"
                  : "bg-card border-line text-inkSecondary hover:border-main/40"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-muted mb-3 px-0.5">
        {activeFilter === "deleted" ? "Deleted history" : "Transactions"} ·{" "}
        <span className="font-bold text-inkSecondary">{filteredRows.length}</span>
      </p>

      {filteredRows.length === 0 ? (
        <div className="text-center py-10 rounded-2xl bg-subtle text-muted text-sm">
          {activeFilter === "deleted"
            ? "No deleted loan entries."
            : activeFilter === "credit"
              ? "No credit transactions."
              : activeFilter === "debit"
                ? "No debit transactions."
                : "No transactions."}
        </div>
      ) : (
        <div className="space-y-3 rounded-2xl bg-subtle p-3 sm:p-4">
          {filteredRows.map((row) => (
            <LoanLogTransactionCard
              key={row._id}
              row={row}
              showParty={showParty}
              base={base}
              navigate={navigate}
              onEditDisbursement={onEditDisbursement}
              onEditRepayment={onEditRepayment}
              onDeleteDisbursement={onDeleteDisbursement}
              onDeleteRepayment={onDeleteRepayment}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LoanTransactionLogCards;
